import React, { memo, useCallback } from 'react';
import { Rect, Circle, Line, Text, Group } from 'react-konva';

export const Shape = memo(({ 
  id,
  type,
  x, 
  y, 
  width = 100,
  height = 100,
  radius = 50,
  fill = '#93c5fd',
  stroke = '#3b82f6',
  strokeWidth = 2,
  text = '',
  isSelected,
  onSelect,
  onDragEnd,
  onTransform,
  onTextEdit,
  points
}) => {
  // Memoized callbacks to prevent re-renders
  const handleSelect = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  const handleDragEnd = useCallback((e) => {
    const node = e.target;
    onDragEnd(id, {
      x: node.x(),
      y: node.y(),
    });
  }, [id, onDragEnd]);

  const handleTransformEnd = useCallback((e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset scale
    node.scaleX(1);
    node.scaleY(1);

    if (onTransform) {
      onTransform(id, {
        x: node.x(),
        y: node.y(),
        width: Math.max(20, node.width() * scaleX),
        height: Math.max(20, node.height() * scaleY),
        radius: type === 'circle' ? Math.max(10, node.radius() * scaleX) : undefined,
      });
    }
  }, [id, type, onTransform]);

  const handleDoubleClick = useCallback(() => {
    if (onTextEdit) {
      onTextEdit(id);
    }
  }, [id, onTextEdit]);

  const commonProps = {
    name: id,
    draggable: true,
    onClick: handleSelect,
    onTap: handleSelect,
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
    stroke: isSelected ? '#3d9168' : stroke,
    strokeWidth: isSelected ? 3 : strokeWidth,
    shadowColor: 'black',
    shadowBlur: 5,
    shadowOpacity: 0.2,
    shadowOffsetX: 2,
    shadowOffsetY: 2,
  };

  if (type === 'rectangle') {
    return (
      <Group>
        <Rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          cornerRadius={4}
          {...commonProps}
          onDblClick={handleDoubleClick}
          onDblTap={handleDoubleClick}
        />
        {text && (
          <Text
            x={x}
            y={y}
            width={width}
            height={height}
            text={text}
            fontSize={16}
            fontFamily="Arial"
            fill="#000"
            align="center"
            verticalAlign="middle"
            wrap="word"
            listening={false}
          />
        )}
      </Group>
    );
  }

  if (type === 'circle') {
    return (
      <Group>
        <Circle
          x={x}
          y={y}
          radius={radius}
          fill={fill}
          {...commonProps}
          onDblClick={handleDoubleClick}
          onDblTap={handleDoubleClick}
        />
        {text && (
          <Text
            x={x - radius}
            y={y - 8}
            width={radius * 2}
            height={16}
            text={text}
            fontSize={16}
            fontFamily="Arial"
            fill="#000"
            align="center"
            verticalAlign="middle"
            wrap="word"
            listening={false}
          />
        )}
      </Group>
    );
  }

  if (type === 'triangle') {
    // Triangle points relative to 0,0 (will be positioned by x,y)
    const trianglePoints = [
      0, -height / 2,
      -width / 2, height / 2,
      width / 2, height / 2,
    ];
    
    return (
      <Group>
        <Line
          x={x}
          y={y}
          points={trianglePoints}
          fill={fill}
          closed
          {...commonProps}
          onDblClick={handleDoubleClick}
          onDblTap={handleDoubleClick}
        />
        {text && (
          <Text
            x={x - width / 2}
            y={y - 8}
            width={width}
            height={16}
            text={text}
            fontSize={16}
            fontFamily="Arial"
            fill="#000"
            align="center"
            verticalAlign="middle"
            wrap="word"
            listening={false}
          />
        )}
      </Group>
    );
  }

  if (type === 'mindmap') {
    return (
      <Group>
        <Rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          cornerRadius={height / 2}
          {...commonProps}
          onDblClick={handleDoubleClick}
          onDblTap={handleDoubleClick}
        />
        {text && (
          <Text
            x={x}
            y={y}
            width={width}
            height={height}
            text={text}
            fontSize={14}
            fontFamily="Arial"
            fontStyle="bold"
            fill="#000"
            align="center"
            verticalAlign="middle"
            wrap="word"
            listening={false}
          />
        )}
      </Group>
    );
  }

  return null;
});
